import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { EventCardWrapper_Event$key } from "@/screens/Home/components/FeaturedEvents/EventsList/components/__generated__/EventCardWrapper_Event.graphql";
import AccordionCard from "@whoosh/design-system/src/components/controls/AccordionCard";
import { Box, Text } from "@whoosh/design-system/src/components/primitive";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { EventCardWrapper } from "../../../Home/components/FeaturedEvents/EventsList/components/EventCardWrapper";
import { EventDetailSeriesInfo_event$key } from "./__generated__/EventDetailSeriesInfo_event.graphql";

type EventDetailSeriesInfoProps = {
  fragmentRef: EventDetailSeriesInfo_event$key | null;
};

export const EventDetailSeriesInfo = ({
  fragmentRef,
}: EventDetailSeriesInfoProps) => {
  const data = useFragment(EventDetailSeriesInfoFragment, fragmentRef);
  const eventSeries = data?.eventSeries;
  const eventNounPlural = getNounOverride(
    eventSeries?.facility?.club?.copyOverrides?.event?.plural,
    t`Events`
  );
  const id = data?.id;

  // Filter out the current event -- and see if we have others in the series
  const otherEventNodes = eventSeries?.events?.edges
    ?.map((edge) => edge?.node ?? null)
    .filter((n) => n?.id !== id);

  // Sort them into upcoming and past events.
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const pastEvents =
    otherEventNodes?.filter(
      (node) => node?.endOn && new Date(node.endOn) < today
    ) ?? [];

  const upcomingEvents =
    otherEventNodes?.filter(
      (node) => !node?.endOn || new Date(node.endOn) >= today
    ) ?? [];

  return (
    <>
      {!!otherEventNodes?.length ? (
        <Text
          variant="mainSmallSemiBold"
          mt={10}
          py={2}
          mb={4}
        >{t`Series`}</Text>
      ) : null}
      {upcomingEvents.length ? (
        <Box mb={4}>
          <AccordionCard
            Title={t`Upcoming ${eventNounPlural} in the Series`}
            InnerContent={<EventCardStack events={upcomingEvents} />}
          />
        </Box>
      ) : null}
      {pastEvents.length ? (
        <AccordionCard
          Title={t`Past ${eventNounPlural} in the Series`}
          InnerContent={<EventCardStack events={pastEvents} />}
        />
      ) : null}
    </>
  );
};

const EventCardStack = ({
  events,
}: {
  events: (EventCardWrapper_Event$key | null)[];
}) => {
  return (
    <>
      {events.map((e, index) => (
        <Box flexShrink={0} key={index} mt={index > 0 ? 4 : 0}>
          <EventCardWrapper fullWidth={true} eventCardWrapperRef={e ?? null} />
        </Box>
      ))}
    </>
  );
};

export const EventDetailSeriesInfoFragment = graphql`
  fragment EventDetailSeriesInfo_event on Event {
    id

    eventSeries {
      facility {
        club {
          copyOverrides {
            event {
              plural
            }
          }
        }
      }
      events {
        edges {
          node {
            id
            endOn
            ...EventCardWrapper_Event
          }
        }
      }
    }
  }
`;
